import { useEffect, useRef, useState } from "react";

/**
 * Gate a skeleton behind a short delay so it never flashes on a fast response.
 *
 *   const showSkeleton = useDelayedFlag(isLoading);
 *
 * `active` true  → returns true only after `delay` ms have elapsed.
 * `active` false → returns false immediately. Ready content is NEVER held back.
 *
 * 250ms is the product default (Section 6). Tune per-call only if a specific
 * screen looks wrong.
 */
export const SKELETON_DELAY_MS = 250;

export default function useDelayedFlag(active, delay = SKELETON_DELAY_MS) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      // Resolve instantly — no trailing "minimum display time". Holding a
      // skeleton on screen after data is ready is the one thing worse than a
      // flash.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setVisible(false);
      return undefined;
    }

    timerRef.current = setTimeout(() => setVisible(true), delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, delay]);

  return visible;
}

/**
 * "Has this region ever rendered real data?" latch.
 *
 * The single most important rule in the loading spec: a screen that has already
 * rendered never goes backwards. Wrap a loading flag with this and a refresh
 * can no longer revert populated content to a skeleton.
 *
 *   const isInitialLoad = useFirstLoadOnly(isLoading, Boolean(data));
 */
export function useFirstLoadOnly(loading, hasData) {
  const everHadData = useRef(false);
  if (hasData) everHadData.current = true;
  return loading && !everHadData.current;
}
